import React from "react";

import { Typography, Box } from "@mui/material";

import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import BlockIcon from "@mui/icons-material/Block";
import Tooltip from "@mui/material/Tooltip";

// Color code for sidebar
const getStageColor = (status) => {
  switch (status) {
    case "1": // Completed – Green
      return {
        border: "#22C55E",
        bg: "#22C55E0D",
        label: "Completed",
      };

    case "2": // N/A – Light Grey (SC)
      return {
        border: "#9CA3AF",
        bg: "#9CA3AF0D",
        label: "N/A",
      };

    case "3": // Unsuccessful – Red (SC)
      return {
        border: "#DC2626",
        bg: "#DC26260D",
        label: "Unsuccessful",
      };

    case "4": // Not Started – Grey
      return {
        border: "#6B7280",
        bg: "#6B72800D",
        label: "Not Started",
      };

    case "5": // On Hold – Orange / Amber
      return {
        border: "#F59E0B",
        bg: "#F59E0B0D",
        label: "On Hold",
      };

    case "0": // In Process – Blue
      return {
        border: "#006CB8",
        bg: "#EBF2FF",
        label: "In Process",
      };
    default:
      return {
        border: "#9CA3AF",
        bg: "#9CA3AF0D",
        label: "N/A",
      };
  }
};

const getIcon = (status, color) => {
  const iconProps = { sx: { color, fontSize: 34 } };

  switch (status) {
    case "Completed":
      return (
        <Tooltip title="Completed" arrow>
          <CheckCircleOutlineIcon {...iconProps} />
        </Tooltip>
      );

    case "In Process":
      return (
        <Tooltip title="In Process" arrow>
          <AccessTimeIcon {...iconProps} />
        </Tooltip>
      );

    case "Not Started":
      return (
        <Tooltip title="Not Started" arrow>
          <PlayCircleOutlineIcon {...iconProps} />
        </Tooltip>
      );

    case "On Hold":
      return (
        <Tooltip title="On Hold" arrow>
          <PauseCircleOutlineIcon {...iconProps} />
        </Tooltip>
      );

    case "Unsuccessful":
      return (
        <Tooltip title="Unsuccessful" arrow>
          <HighlightOffIcon {...iconProps} />
        </Tooltip>
      );

    case "N/A":
      return (
        <Tooltip title="Not Applicable" arrow>
          <BlockIcon {...iconProps} />
        </Tooltip>
      );

    default:
      return null;
  }
};


const getColorStyles = (status) => {
  switch (status) {
    case "1": // Completed – Green
      return {
        border: "#22C55E",
        bg: "#22C55E0D",
        label: "Completed",
      };

    case "2": // N/A – Light Grey (SC)
      return {
        border: "#9CA3AF",
        bg: "#9CA3AF0D",
        label: "N/A",
      };

    case "3": // Unsuccessful – Red (SC)
      return {
        border: "#DC2626",
        bg: "#DC26260D",
        label: "Unsuccessful",
      };

    case "4": // Not Started – Grey
      return {
        border: "#6B7280",
        bg: "#6B72800D",
        label: "Not Started",
      };

    case "5": // On Hold – Orange / Amber
      return {
        border: "#F59E0B",
        bg: "#F59E0B0D",
        label: "On Hold",
      };

    case "0": // In Process – Blue
      return {
        border: "#006CB8",
        bg: "#EBF2FF",
        label: "In Process",
      };
    default:
      return {
        border: "#9CA3AF",
        bg: "#9CA3AF0D",
        label: "N/A",
      };
  }
};

function getExpiryStatus(field) {
  if (field.RPF_Type !== "Date") return null;

  // 🔹 Check if expiry is enabled
  if (field.RPF_IsExpiry == undefined) {
    return null;
  }

  // 1. Parse expiry period
  let expiryDays = 0;
  if (field.RPF_ExpiryPeriod && field.RPF_IsExpiry != undefined) {
    try {
      expiryDays = Number(field.RPF_ExpiryPeriod.toString().replace(/"/g, ""));
    } catch {
      expiryDays = 0;
    }
  }

  // 2. Parse the base date (RPF_Value)
  if (!field.RPF_Value) return null;
  const baseDate = new Date(field.RPF_Value);
  if (isNaN(baseDate)) return null;

  // 3. Calculate expiry date
  const expiryDate = new Date(baseDate);
  expiryDate.setDate(baseDate.getDate() + expiryDays);

  // 4. Compare with today
  const today = new Date();
  const diffTime = expiryDate - today;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  // Helper for singular/plural
  const dayLabel = (n) => (Math.abs(n) === 1 ? "day" : "days");

  // 5. Handle result
  if (diffDays > 30) {
    return null; // don’t show if more than 30 days left
  } else if (diffDays > 0) {
    return {
      value: `${field.RPF_Name}: ${diffDays} ${dayLabel(diffDays)} pending`,
      fontSize: "16px",
      color: "black",
    };
  } else if (diffDays === 0) {
    return {
      value: `${field.RPF_Name}: Expires today`,
      fontSize: "16px",
      color: "black",
    };
  } else if (diffDays >= -60) {
    return {
      value: `${field.RPF_Name}: Expired ${Math.abs(diffDays)} ${dayLabel(
        diffDays
      )} ago`,
      fontSize: "16px",
      color: "red",
    };
  } else {
    return null; // don’t show if expired more than 60 days ago
  }
}

function SideStageCard({ originalValue2 = [], mx = null }) {
  return (
    <>
      {originalValue2?.stages?.map((stage, index) => {
        const hasSubProcedures =
          Array.isArray(stage.subProcedures) && stage.subProcedures.length > 0;

        let stageStatus = "4"; // default: in progress

        const subComponents = hasSubProcedures
          ? stage.subProcedures.map((sub) => {
            const fields = sub?.fields || [];
            const statusField = fields.find(
              (field) => field?.RPF_Type === "IsCompleted"
            );
            const status = [statusField?.RPF_Value].flat()[0] || "4";
            return {
              ...sub,
              isCompleted: status,
            };
          })
          : [];

        if (hasSubProcedures) {
          const statuses = subComponents.map((s) => s.isCompleted);

          if (statuses.every((s) => s == 1)) {
            // All completed
            stageStatus = "1";
          } else if (statuses.every((s) => s == 2)) {
            // All N/A
            stageStatus = "2";
          } else if (statuses.some((s) => s == 3)) {
            // Any unsuccessful
            stageStatus = "3";
          } else if (statuses.some((s) => s == 5)) {
            // Any on hold
            stageStatus = "5";
          } else if (statuses.every((s) => s == 4)) {
            // All not started
            stageStatus = "4";
          } else {
            // Mixed or in progress
            stageStatus = "";
          }
        } else {
          const stageField = stage?.fields?.find(
            (field) => field?.RPF_Type === "IsCompleted"
          );

          stageStatus = [stageField?.RPF_Value].flat()[0] || "4";
        }

        const subStatus = stageStatus;
        const stageColor = getStageColor(stageStatus);

        // _______________________________________________________________________________________________________________________

        const fieldsOnly = stage?.fields || [];

        const stageStatusFieldsMain = fieldsOnly.filter((field) =>
          field?.RPF_Name?.trim()?.toLowerCase().includes("status")
        );

        const allStageStatusFields = [...stageStatusFieldsMain];

        return (
          <Box
            key={index}
            sx={{
              p: 2,
              mb: 2,
              borderRadius: 2,
              backgroundColor: stageColor.bg,
              borderLeft: stageStatus
                ? `5px solid ${stageColor.border}`
                : "5px solid lightgray",
              boxShadow: "0px 2px 5px rgba(0,0,0,0.1)",
              margin: "15px",
              cursor: "pointer",
              mx: mx
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              {subStatus &&
                getIcon(
                  subStatus == 1
                    ? "Completed"
                    : subStatus == 2
                      ? "N/A"
                      : subStatus == 3
                        ? "Unsuccessful"
                        : subStatus == 4
                          ? "Not Started"
                          : subStatus == 5
                            ? "On Hold"
                            : "In Process",
                  stageColor.border
                )}


              <Box>
                <Typography fontWeight="bold">{stage.RPS_Name}</Typography>

                {allStageStatusFields
                  .filter((field) => field.RPF_Value) // only keep fields with a value
                  .map((field, index) => (
                    <Typography
                      key={index}
                      fontWeight={600}
                      sx={{
                        textTransform: "capitalize",
                        fontFamily: "Geist, sans-serif",
                        fontSize: "14px",
                        // color: stageColor.border,
                        mb: 0.5, // spacing between lines
                      }}
                    >
                      {field.RPF_Name} : {field.RPF_Value || "---"}
                    </Typography>
                  ))}

                {/* New Implementation - 09-09-25 - Expiry Date */}

                {stage?.fields?.map((field) => {
                  const status = getExpiryStatus(field);

                  return (
                    <Box key={field.RPF_Id}>
                      {status?.value && (
                        <Typography
                          sx={{
                            fontSize: status?.fontSize,
                            color: status?.color,
                            textTransform: "Capitalize",
                          }}
                        >
                          {status?.value}
                        </Typography>
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Box>

            {hasSubProcedures &&
              subComponents.map((sub, subIndex) => {
                const subColor = getStageColor(sub.isCompleted);
                const statusKey = String(sub.isCompleted ?? "4");
                const statusMap = getColorStyles(statusKey);

                // ✅ Status fields ONLY for this sub-procedure
                const subStatusFields = (sub?.fields || [])
                  .filter((field) =>
                    field?.RPF_Name?.trim()?.toLowerCase().includes("status")
                  )
                  .filter((field) => field?.RPF_Value); // only non-empty values

                return (
                  <Box
                    key={`sub-${subIndex}`}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      mt: 1.5,
                      p: 1.5,
                      borderRadius: 2,
                      backgroundColor: subColor.bg,
                      borderLeft: `4px solid ${subColor.border}`,
                    }}
                  >
                    {getIcon(statusMap.label, statusMap.border)}

                    <Box>
                      <Typography fontWeight={500}>{sub.RSP_Name}</Typography>

                      {subStatusFields.map((field, index) => (
                        <Typography
                          key={index}
                          fontWeight={600}
                          sx={{
                            textTransform: "capitalize",
                            fontFamily: "Geist, sans-serif",
                            fontSize: "14px",
                            // color: subColor?.border,
                            mb: 0.5,
                          }}
                        >
                          {field.RPF_Name} : {field.RPF_Value || "---"}
                        </Typography>
                      ))}

                      {/* New Implementation - 09-09-25 - Expiry Date */}

                      {sub?.fields?.map((field) => {
                        const status = getExpiryStatus(field);
                        return (
                          <Box key={field.RPF_Id}>
                            {status && (
                              <Typography
                                sx={{
                                  fontSize: status?.fontSize,
                                  color: status?.color,
                                  textTransform: "Capitalize",
                                }}
                              >
                                {status?.value}
                              </Typography>
                            )}
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                );
              })}
          </Box>
        );
      })}
    </>
  );
}

export default SideStageCard;
